<template>
  <div class="modal fade show" style="display: block" tabindex="-1" aria-hidden="false">
    <div class="modal-dialog modal-dialog-centered modal-width-premium-compact">
      <div class="modal-content">
        <div class="modal-header justify-content-end">
          <button type="button" class="close-link" @click="$emit('close')">
            <i class="ti ti-close"></i>
          </button>
        </div>
        <div class="modal-body padd-modal-premium">
          <div class="title-modal">
            <img :src="editIcon" />
            <h3>{{ $t("reminder.edit_modal_title") }}</h3>
          </div>

          <form class="mt-4 modal-form-premium" @submit.prevent="handleSubmit">
            <div class="form-container-premium">
              <div class="form-row-premium mb-2">
                <div class="input-form input-form-half">
                  <label class="label-control">{{ $t("reminder.owner_label") }}*</label>
                  <div
                    class="form-control d-flex align-items-center"
                    style="background-color: #f8f9fa; min-height: 48px; border-radius: 35px; padding: 12px 20px"
                  >
                    <span>{{ ownerDisplayName }}</span>
                  </div>
                  <small class="text-muted">{{ $t("reminder.owner_readonly_note") }}</small>
                </div>
                <div class="input-form input-form-half">
                  <label class="label-control">{{ $t("reminder.start_date_label") }}*</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="form.dateMiseEnPlace"
                    :class="{ 'is-invalid': errors.dateMiseEnPlace }"
                    required
                  />
                  <div v-if="errors.dateMiseEnPlace" class="invalid-feedback">
                    {{ errors.dateMiseEnPlace }}
                  </div>
                </div>
              </div>
              <div class="mb-2">
                <div class="input-form input-form-full">
                  <label class="label-control">{{ $t("reminder.product_label") }}*</label>

                  <div v-if="productsLoading" class="d-flex align-items-center justify-content-center py-3">
                    <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                    <span>{{ $t("reminder.loading_products") }}</span>
                  </div>

                  <div v-else-if="!hasProducts" class="alert alert-info">
                    {{ $t("reminder.no_products_available") }}
                  </div>

                  <div v-else class="product-selector">
                    <div class="position-relative">
                      <div
                        class="form-control d-flex justify-content-between align-items-center"
                        @click="toggleProductDropdown"
                        style="cursor: pointer; min-height: 60px"
                        :class="{ 'is-invalid': errors.productVariantId }"
                      >
                        <div class="d-flex gap-3 align-items-center">
                          <img
                            :src="getProductImage(selectedProduct)"
                            alt="img"
                            v-if="getProductImage(selectedProduct)"
                            style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px"
                          />
                          <div v-if="selectedProduct">
                            <div class="fw-bold">{{ selectedProduct.name }}</div>
                            <div class="text-muted small">{{ selectedProduct.gamme }}</div>
                          </div>
                          <span v-else class="text-muted">{{ $t("reminder.select_product") }}</span>
                        </div>
                        <i
                          class="ti ti-angle-down"
                          :class="{ 'rotate-180': showProductDropdown }"
                          style="transition: transform 0.2s"
                        ></i>
                      </div>

                      <div
                        v-show="showProductDropdown"
                        class="position-absolute w-100 bg-white border rounded shadow-lg"
                        style="top: 100%; z-index: 1050; max-height: 300px; overflow-y: auto"
                      >
                        <div
                          v-for="product in availableProducts"
                          :key="product.id"
                          class="p-3 border-bottom hover-bg-light d-flex gap-3 align-items-center"
                          style="cursor: pointer"
                          @click="selectProduct(product)"
                        >
                          <img
                            :src="getProductImage(product)"
                            alt="img"
                            style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px"
                            v-if="getProductImage(product)"
                          />
                          <div>
                            <div class="fw-bold">{{ product.name }}</div>
                            <div class="text-muted small">{{ product.gamme_supplyvet || '' }}</div>
                          </div>
                        </div>
                        <div v-if="availableProducts.length === 0" class="p-3 text-center text-muted">
                          {{ $t("reminder.no_products_available") }}
                        </div>
                      </div>
                    </div>
                    <div v-if="errors.productVariantId" class="invalid-feedback">
                      {{ errors.productVariantId }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="selectedProduct" class="mb-2">
                <div class="input-form input-form-full">
                  <label class="label-control">{{ $t("reminder.variant_label") }}*</label>
                  <select
                    v-model="form.productVariantId"
                    class="form-select"
                    :class="{ 'is-invalid': errors.productVariantId }"
                    required
                  >
                    <option value="" disabled>{{ $t("reminder.select_variant") }}</option>
                    <option
                      v-for="variant in availableVariants"
                      :key="variant.id"
                      :value="variant.id"
                      :disabled="!variant.inStock && !(variant.datePrecommandeSupplyvetEsupplyvet && new Date(variant.datePrecommandeSupplyvetEsupplyvet) > new Date())"
                    >
                      {{ formatVariantDisplay(variant) }}
                      {{ (!variant.inStock && !(variant.datePrecommandeSupplyvetEsupplyvet && new Date(variant.datePrecommandeSupplyvetEsupplyvet) > new Date())) ? ` (${$t("reminder.out_of_stock")})` : "" }}
                    </option>
                  </select>
                  <div v-if="errors.productVariantId" class="invalid-feedback">
                    {{ errors.productVariantId }}
                  </div>
                </div>
              </div>
              <div class="form-row-premium mb-2">
                <div class="input-form input-form-half">
                  <label class="label-control">{{ $t("reminder.daily_ration_label") }}*</label>
                  <div class="position-relative">
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="form.rationJournaliere"
                        :class="{ 'is-invalid': errors.rationJournaliere }"
                        min="1"
                        max="9999"
                        step="1"
                        placeholder="Entrer la quantité"
                        required
                      />
                      <span class="input-group-text">g</span>
                    </div>
                    <i
                      class="ti ti-weight position-absolute"
                      style="right: 45px; top: 50%; transform: translateY(-50%); color: #ff6b35"
                    ></i>
                  </div>
                  <div v-if="errors.rationJournaliere" class="invalid-feedback">
                    {{ errors.rationJournaliere }}
                  </div>
                </div>
                <div class="input-form input-form-half">
                  <label class="label-control">{{ $t("reminder.notification_types_label") }}*</label>
                  <div class="d-flex align-items-center gap-4 mt-2">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="editNotificationEmail"
                        value="email"
                        v-model="form.notificationType"
                        :class="{ 'is-invalid': errors.notificationType }"
                      />
                      <label class="form-check-label" for="editNotificationEmail">
                        {{ $t("reminder.email") }}
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="editNotificationSMS"
                        value="sms"
                        v-model="form.notificationType"
                        :class="{ 'is-invalid': errors.notificationType }"
                      />
                      <label class="form-check-label" for="editNotificationSMS">
                        {{ $t("reminder.sms") }}
                      </label>
                    </div>
                  </div>
                  <div v-if="errors.notificationType" class="invalid-feedback d-block">
                    {{ errors.notificationType }}
                  </div>
                </div>
              </div>
              <div v-if="calculatedData.bagDuration" class="alert alert-info">
                <h6>{{ $t("reminder.automatic_calculations") }}</h6>
                <ul class="mb-0">
                  <li>
                    {{ $t("reminder.bag_duration") }}: <strong>{{ calculatedData.bagDuration }}</strong>
                  </li>
                  <li>
                    {{ $t("reminder.end_date") }}: <strong>{{ calculatedData.endDate }}</strong>
                  </li>
                </ul>
              </div>

              <div v-if="errors.general" class="alert alert-danger">
                {{ errors.general }}
              </div>
              <div class="d-flex justify-content-center gap-3 mt-4">
                <button type="button" class="btn btn-outline-orange btn-premium" @click="$emit('close')">
                  {{ $t("reminder.cancel") }}
                </button>
                <button type="submit" class="btn btn-orange btn-premium" :disabled="loading">
                  <span v-if="loading"> <i class="ti ti-loader"></i> {{ $t("reminder.updating") }} </span>
                  <span v-else>{{ $t("reminder.update") }}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>

<script>
  import editIcon from "@/assets/icons/edit.svg";
  import { useProprietairesStore } from "@/stores/esupplyvet/proprietairesStore";
  import { useProductsStore } from "@/stores/productsStore";
  import { useAnimauxStore } from "@/stores/animauxStore";

  export default {
    name: "ReminderEditModal",
    props: {
      reminder: {
        type: Object,
        required: true,
      },
    },
    emits: ["close", "submit"],
    data() {
      return {
        editIcon,
        form: {
          ownerId: null,
          productVariantId: null,
          rationJournaliere: "",
          dateMiseEnPlace: "",
          notificationType: [],
        },
        errors: {},
        loading: false,
        productsLoading: false,
        selectedProduct: null,
        showProductDropdown: false,
        availableProducts: [],
        availableVariants: [],
      };
    },
    computed: {
      proprietairesStore() {
        return useProprietairesStore();
      },
      productsStore() {
        return useProductsStore();
      },
      animauxStore() {
        return useAnimauxStore();
      },
      ownerDisplayName() {
        if (this.reminder?.proprietaire) {
          const owner = this.reminder.proprietaire;

          if (owner.nom && owner.nom.trim()) {
            return owner.nom.trim();
          }

          if (owner.email) {
            return owner.email;
          }
        }

        if (this.reminder?.ownerId && this.proprietairesStore.formattedProprietaires) {
          const ownerFromStore = this.proprietairesStore.formattedProprietaires.find(
            owner => owner.id === this.reminder.ownerId
          );

          if (ownerFromStore) {
            return ownerFromStore.fullName || ownerFromStore.email || `Propriétaire #${this.reminder.ownerId}`;
          }
        }

        if (this.reminder?.ownerId) {
          return `Propriétaire #${this.reminder.ownerId}`;
        }

        return "Propriétaire inconnu";
      },
      hasProducts() {
        return this.availableProducts.length > 0;
      },
      isProductsLoading() {
        return this.productsStore.rationLoading;
      },
      calculatedData() {
        if (!this.form.rationJournaliere || !this.selectedVariant || !this.form.dateMiseEnPlace) {
          return {};
        }

        const dailyRation = parseFloat(this.form.rationJournaliere);
        const variantWeight = parseFloat(this.selectedVariant.weight) * 1000; // Convertir kg en g

        if (dailyRation > 0 && variantWeight > 0) {
          const duration = Math.round(variantWeight / dailyRation);
          const startDate = new Date(this.form.dateMiseEnPlace);
          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + duration);

          return {
            bagDuration: `${duration} jours`,
            endDate: endDate.toLocaleDateString("fr-FR"),
          };
        }

        return {};
      },
      selectedVariant() {
        if (!this.form.productVariantId) return null;
        return this.availableVariants.find(v => v.id === this.form.productVariantId);
      },
    },
    async mounted() {
      if (this.reminder) {
        await this.initializeForm();
      }
    },
    watch: {
      reminder: {
        handler(newReminder) {
          if (newReminder) {
            this.initializeForm();
          }
        },
        immediate: true,
      },
    },
    methods: {
      async initializeForm() {
        if (!this.reminder) return;

        try {
          this.form = {
            ownerId: this.reminder.ownerId || null,
            productVariantId: this.reminder.variantId || this.reminder.productVariantId,
            rationJournaliere: this.extractRationValue(this.reminder.rationJour || this.reminder.rationJournaliere),
            dateMiseEnPlace: this.convertDateFormat(this.reminder.dateMiseEnPlace),
            notificationType: [...(this.reminder.notificationType || [])],
          };

          await this.ensureProprietairesLoaded();
          await this.loadProductsForOwner();

          if (this.form.productVariantId) {
            await this.preselectCurrentProduct();
          }
        } catch (error) {
          console.error("Erreur lors de l'initialisation du formulaire:", error);
          this.errors.general = "Erreur lors du chargement des données";
        }
      },

      extractRationValue(rationString) {
        if (!rationString) return "";

        if (typeof rationString === "number") {
          return rationString;
        }

        if (typeof rationString === "string") {
          const match = rationString.match(/(\d+(?:\.\d+)?)/);
          return match ? parseFloat(match[1]) : "";
        }

        return "";
      },

      convertDateFormat(dateString) {
        if (!dateString) {
          return new Date().toISOString().split("T")[0];
        }

        if (dateString.includes("/")) {
          const [day, month, year] = dateString.split("/");
          return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        }

        return dateString.split(" ")[0];
      },

      async ensureProprietairesLoaded() {
        try {
          if (
            !this.proprietairesStore.formattedProprietaires ||
            this.proprietairesStore.formattedProprietaires.length === 0
          ) {
            await this.proprietairesStore.fetchProprietaires();
          }
        } catch (error) {
          console.error("Erreur lors du chargement des propriétaires:", error);
        }
      },

      loadProductsForOwner() {
        this.productsLoading = this.productsStore.rationLoading;
        this.availableProducts = this.productsStore.reminderProducts || [];

        if (this.availableProducts.length === 0) {
          console.warn("Aucun produit disponible dans le modal d'édition - vérifier le préchargement dans RappelView");
        }
      },

      async preselectCurrentProduct() {
        const variantId = parseInt(this.form.productVariantId);

        const currentProduct = this.availableProducts.find(
          product =>
            product.variantsDetails && product.variantsDetails.some(variant => parseInt(variant.id) === variantId)
        );

        if (currentProduct) {
          this.selectedProduct = currentProduct;
          this.availableVariants = (currentProduct.variantsDetails || []).filter(variant => variant.unity === "u");
        } else {
          console.warn("Aucun produit trouvé pour variantId:", variantId);
        }
      },

      selectProduct(product) {
        this.selectedProduct = product;
        this.form.productVariantId = null;
        this.showProductDropdown = false;

        this.availableVariants = (product.variantsDetails || []).filter(variant => variant.unity === "u");

        if (this.availableVariants.length > 0 && !this.form.productVariantId) {
          const firstAvailable = this.availableVariants.find(v => 
            (v.inStock || (v.datePrecommandeSupplyvetEsupplyvet && new Date(v.datePrecommandeSupplyvetEsupplyvet) > new Date())) && 
            v.enabled !== false
          );
          if (firstAvailable) {
            this.form.productVariantId = firstAvailable.id;
          }
        }
      },

      toggleProductDropdown() {
        this.showProductDropdown = !this.showProductDropdown;
      },

      getProductImage(product) {
        if (!product) return null;

        if (product.imagesDetails && product.imagesDetails.length > 0) {
          return product.imagesDetails[0].path;
        }
        if (product.images && product.images.length > 0) {
          return product.images[0].path;
        }
        return null;
      },

      formatPrice(price) {
        if (!price) return "Prix non disponible";
        return `${(price / 100).toFixed(2)} €`;
      },

      formatVariantDisplay(variant) {
        if (!variant) return "";

        let display = variant.name || "Variant";

        if (variant.weight) {
          display += ` - ${variant.weight}kg`;
        }

        if (variant.currentChannelPricings?.price) {
          display += ` - ${this.productsStore.formatPrice(variant.currentChannelPricings.price)}`;
        }

        return display;
      },

      validateForm() {
        this.errors = {};
        let isValid = true;

        if (!this.form.productVariantId) {
          this.errors.productVariantId = "Le produit est obligatoire";
          isValid = false;
        }

        if (!this.form.rationJournaliere || this.form.rationJournaliere <= 0) {
          this.errors.rationJournaliere = "La ration journalière doit être supérieure à 0";
          isValid = false;
        }

        if (!this.form.dateMiseEnPlace) {
          this.errors.dateMiseEnPlace = "La date de mise en place est obligatoire";
          isValid = false;
        }

        if (!this.form.notificationType || this.form.notificationType.length === 0) {
          this.errors.notificationType = "Au moins un type de notification est requis";
          isValid = false;
        }

        return isValid;
      },

      async handleSubmit() {
        if (!this.validateForm()) {
          return;
        }

        try {
          this.loading = true;
          this.errors = {};

          const updateData = {
            ownerId: parseInt(this.form.ownerId),
            productVariantId: parseInt(this.form.productVariantId),
            rationJournaliere: this.form.rationJournaliere.toString(),
            dateMiseEnPlace: this.form.dateMiseEnPlace,
            notificationType: this.form.notificationType,
          };

          this.$emit("submit", updateData);
        } catch (error) {
          console.error("Erreur lors de la soumission:", error);
          this.errors.general = "Une erreur est survenue lors de la modification du rappel";
        } finally {
          this.loading = false;
        }
      },
    },
  };
</script>

<style scoped>
  .modal-width-premium {
    max-width: 75% !important;
  }

  .modal-width-premium-compact {
    max-width: 65% !important;
    margin: 0 auto !important;
    padding: 0 25px !important;
  }

  .padd-modal-premium {
    padding: 25px 35px 35px !important;
  }

  .modal-form-premium {
    max-width: 100%;
  }

  .form-container-premium {
    width: 100%;
    max-width: none;
  }

  .form-row-premium {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    width: 100%;
  }

  .input-form-full {
    width: 100% !important;
    margin-bottom: 12px !important;
  }

  .input-form-half {
    width: 100% !important;
    flex: 1;
    margin-bottom: 12px !important;
  }

  .input-form-full .form-control,
  .input-form-half .form-control,
  .input-form-full .form-select,
  .input-form-half .form-select {
    min-height: 48px;
    padding: 12px 20px;
    font-size: 15px;
    border-radius: 35px;
    border: 1px solid #aeb1ba;
    transition: all 0.3s ease;
  }

  .input-form-full .form-control:focus,
  .input-form-half .form-control:focus,
  .input-form-full .form-select:focus,
  .input-form-half .form-select:focus {
    border-color: #1e347d;
    box-shadow: 0 0 0 0.2rem rgba(30, 52, 125, 0.15);
    outline: none;
  }

  .input-form-full .label-control,
  .input-form-half .label-control {
    font-size: 15px;
    font-weight: 500;
    color: #363739;
    margin-bottom: 8px;
    display: block;
  }

  .btn-premium {
    min-width: 140px !important;
    padding: 12px 30px !important;
    font-size: 15px !important;
    font-weight: 600 !important;
    border-radius: 35px !important;
    transition: all 0.3s ease !important;
  }

  .btn-premium:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .product-selector .form-control {
    cursor: pointer;
    min-height: 60px;
    border-radius: 35px;
    padding: 15px 20px;
    transition: all 0.3s ease;
  }

  .product-selector .form-control:hover {
    border-color: #1e347d;
    box-shadow: 0 0 0 0.1rem rgba(30, 52, 125, 0.1);
  }

  .product-selector .position-absolute {
    border-radius: 15px;
    border: 1px solid #dee2e6;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .product-selector .hover-bg-light:hover {
    background-color: #f8f9fa;
    transition: background-color 0.2s ease;
  }

  .input-group {
    border-radius: 35px;
    overflow: hidden;
    border: 1px solid #aeb1ba;
    transition: all 0.3s ease;
  }

  .input-group:focus-within {
    border-color: #1e347d;
    box-shadow: 0 0 0 0.2rem rgba(30, 52, 125, 0.15);
  }

  .input-group .form-control {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .input-group .form-control:focus {
    border: none;
    box-shadow: none;
  }

  .input-group-text {
    background-color: #f8f9fa;
    border: none;
    color: #6c757d;
    font-weight: 500;
    padding: 12px 15px;
  }

  .form-check {
    margin-bottom: 0;
  }

  .form-check-input {
    width: 18px;
    height: 18px;
    margin-top: 0.125em;
    border-radius: 4px;
    border: 2px solid #aeb1ba;
    transition: all 0.3s ease;
  }

  .form-check-input:checked {
    background-color: #ec621a !important;
    border-color: #ec621a !important;
  }

  .form-check-input:focus {
    border-color: #ec621a;
    box-shadow: 0 0 0 0.2rem rgba(236, 98, 26, 0.15);
  }

  .form-check-label {
    font-size: 15px;
    font-weight: 500;
    color: #363739;
    margin-left: 8px;
  }

  .alert {
    border-radius: 15px;
    border: none;
    padding: 15px 20px;
    margin-bottom: 20px;
  }

  .alert-info {
    background-color: #e3f2fd;
    color: #1565c0;
    border-left: 4px solid #2196f3;
  }

  .alert-danger {
    background-color: #ffebee;
    color: #c62828;
    border-left: 4px solid #f44336;
  }

  .alert-secondary {
    background-color: #f8f9fa;
    color: #495057;
    border-left: 4px solid #6c757d;
  }

  @media (max-width: 768px) {
    .modal-width-premium {
      max-width: 95% !important;
    }

    .modal-width-premium-compact {
      max-width: 95% !important;
      padding: 0 15px !important;
    }

    .padd-modal-premium {
      padding: 20px 20px 25px !important;
    }

    .form-row-premium {
      flex-direction: column;
      gap: 0;
    }

    .input-form-half {
      width: 100% !important;
    }

    .btn-premium {
      min-width: 120px !important;
      padding: 10px 25px !important;
    }
  }

  .hover-bg-light:hover {
    background-color: #f8f9fa;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  .product-selector {
    position: relative;
  }

  .product-selector .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1050;
    max-height: 300px;
    overflow-y: auto;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .product-selector .dropdown-item {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #dee2e6;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .product-selector .dropdown-item:hover {
    background-color: #f8f9fa;
  }

  .product-selector .dropdown-item:last-child {
    border-bottom: none;
  }

  .product-selector img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .ti-angle-down {
    transition: transform 0.2s ease;
  }

  .ti-angle-down.rotate-180 {
    transform: rotate(180deg);
  }
</style>
